<template>
  <section class="mt-12 w-full">
    <UTabs v-model="activeTab" :items="giftsNav" :ui="tabsUI">
      <template #activities>
        <div
          v-if="!isLoading && activityGifts.length === 0"
          class="mt-4 text-center text-neutral-400"
        >
          There are currently no activity gifts available at
          {{ blok.headline }}.
        </div>

        <div
          v-if="!isLoading && activityGifts.length > 0"
          class="mt-4 grid gap-6"
        >
          <LazyMarketingTabsProductsGiftsList
            :gifts="activityGifts"
            :type="type"
            :enable-gift-redemption="enableGiftRedemption"
          />
        </div>

        <div v-if="isLoading" class="mt-4 grid gap-6">
          <LazyMarketingCardGiftLoading v-for="i in 3" :key="i" />
        </div>
      </template>

      <template #experience>
        <div
          v-if="!isLoading && experienceGifts.length === 0"
          class="mt-4 text-center text-neutral-400"
        >
          There are currently no experience gifts available at
          {{ blok.headline }}.
        </div>

        <div
          v-if="!isLoading && experienceGifts.length > 0"
          class="mt-4 grid gap-6"
        >
          <LazyMarketingTabsProductsGiftsList
            :gifts="experienceGifts"
            :type="type"
            :enable-gift-redemption="enableGiftRedemption"
          />
        </div>

        <div v-if="isLoading" class="mt-4 grid gap-6">
          <div v-for="i in 3" :key="i">
            <LazyMarketingCardGiftLoading />
          </div>
        </div>
      </template>

      <template #credit>
        <div
          v-if="!isLoading && creditGifts.length === 0"
          class="mt-4 text-center text-neutral-400"
        >
          There are currently no credit gifts available at
          {{ blok.headline }}.
        </div>

        <div
          v-if="!isLoading && creditGifts.length > 0"
          class="mt-4 grid gap-6"
        >
          <LazyMarketingTabsProductsGiftsList
            :gifts="creditGifts"
            :type="type"
            :enable-gift-redemption="enableGiftRedemption"
          />
        </div>

        <div v-if="isLoading" class="mt-4 grid gap-6">
          <div v-for="i in 3" :key="i">
            <LazyMarketingCardGiftLoading />
          </div>
        </div>
      </template>
    </UTabs>
  </section>
</template>

<script setup lang="ts">
import type { GiftStoryblok } from '~/types/component-types-sb';

const props = defineProps<{
  blok: {
    headline?: string;
  };
  uuid: string;
  type: string;
  enableGiftRedemption: boolean;
}>();

const emit = defineEmits<{
  (e: 'gifts-false'): void;
  (e: 'gifts-count', count: number): void;
}>();

const { fetchGifts, isLoading } = useStoryblokClient();
const route = useRoute();

// Compute the UI styles based on the current route
const tabsUI = computed(() => {
  const baseUI = {
    list: 'bg-neutral-900/60 border border-neutral-800 backdrop-blur-lg overflow-hidden rounded-2xl',
    trigger: 'disabled:!text-neutral-600 disabled:text-xs',
    label: 'text-xs sm:text-sm font-extrabold truncate',
  };

  // If route is /gifts, modify the list style
  if (route.path.includes('/gifts')) {
    return {
      root: 'px-2 sm:px-0',
      list: 'bg-neutral-900/40 ring ring-yellow-400 backdrop-blur-lg max-w-xl z-[10] max-auto sticky top-28 lg:top-40',
      leadingIcon: 'hidden sm:block',
      trigger: 'disabled:!text-neutral-600 disabled:text-xs',
      label: 'font-bold',
      content: 'px-4 sm:px-8',
    };
  }

  return baseUI;
});

const activeTab = ref('0');

const giftsNav = ref([
  {
    label: 'Activities',
    slot: 'activities',
    disabled: false,
  },
  {
    label: 'Experiences',
    slot: 'experience',
    disabled: false,
  },
  {
    label: 'Credits',
    slot: 'credit',
    disabled: false,
  },
]);

// Function to disable a tab by slot
const disableItemBySlot = (
  slotToDisable: 'activities' | 'experience' | 'credit'
) => {
  giftsNav.value = giftsNav.value.map((item) => {
    if (item.slot === slotToDisable) {
      return { ...item, disabled: true };
    }
    return item;
  });
};

// Function to update the active tab based on availability of gifts
const updateActiveTab = () => {
  if (activityGifts.value.length > 0) {
    activeTab.value = '0';
  } else if (experienceGifts.value.length > 0) {
    activeTab.value = '1';
  } else if (creditGifts.value.length > 0) {
    activeTab.value = '2';
  }
};

const activityGifts = ref<GiftStoryblok[]>([]);
const experienceGifts = ref<GiftStoryblok[]>([]);
const creditGifts = ref<GiftStoryblok[]>([]);

// Load experience, activity, and credit gifts and set the active tab accordingly
const loadGifts = async () => {
  try {
    const [stories] = await Promise.all([fetchGifts()]);

    if (props.type === 'location') {
      activityGifts.value = stories.filter(
        (gift) =>
          gift.content.gift_type === 'activity' &&
          gift.content.locations?.some(
            (location: { content: unknown; uuid: string }) =>
              location.content && location.uuid === props.uuid
          )
      );
      experienceGifts.value = stories.filter(
        (gift) =>
          gift.content.gift_type === 'experience' &&
          gift.content.locations?.some(
            (location: { content: unknown; uuid: string }) =>
              location.content && location.uuid === props.uuid
          )
      );
      creditGifts.value = stories.filter(
        (gift) =>
          gift.content.gift_type === 'credit' &&
          gift.content.locations?.some(
            (location: { content: unknown; uuid: string }) =>
              location.content && location.uuid === props.uuid
          )
      );
    } else if (props.type === 'activity') {
      activityGifts.value = stories.filter(
        (gift) =>
          gift.content.gift_type === 'activity' &&
          gift.content.activities?.some(
            (activity: { content: unknown; uuid: string }) =>
              activity.content && activity.uuid === props.uuid
          )
      );
      experienceGifts.value = stories.filter(
        (gift) =>
          gift.content.gift_type === 'experience' &&
          gift.content.activities?.some(
            (activity: { content: unknown; uuid: string }) =>
              activity.content && activity.uuid === props.uuid
          )
      );
      creditGifts.value = stories.filter(
        (gift) =>
          gift.content.gift_type === 'credit' &&
          gift.content.activities?.some(
            (activity: { content: unknown; uuid: string }) =>
              activity.content && activity.uuid === props.uuid
          )
      );
    } else {
      activityGifts.value = stories.filter(
        (gift) => gift.content.gift_type === 'activity'
      );
      experienceGifts.value = stories.filter(
        (gift) => gift.content.gift_type === 'experience'
      );
      creditGifts.value = stories.filter(
        (gift) => gift.content.gift_type === 'credit'
      );
    }

    // Disable tabs if no gifts are found
    if (activityGifts.value.length === 0) {
      disableItemBySlot('activities');
    }
    if (experienceGifts.value.length === 0) {
      disableItemBySlot('experience');
    }
    if (creditGifts.value.length === 0) {
      disableItemBySlot('credit');
    }

    // Set the active tab based on available gifts
    updateActiveTab();

    if (activityGifts.value || experienceGifts.value || creditGifts.value) {
      emit(
        'gifts-count',
        activityGifts.value.length +
          experienceGifts.value.length +
          creditGifts.value.length
      );
    }

    // Emit 'gifts-false' if no gifts are available
    if (
      activityGifts.value.length === 0 &&
      experienceGifts.value.length === 0 &&
      creditGifts.value.length === 0
    ) {
      emit('gifts-false');
    }
  } catch (error) {
    console.error(error);
  }
};

await loadGifts();
</script>
